<template lang='pug'>
  v-dialog(v-model='open')
    v-card
      v-card-title
        v-row(no-gutters)
          v-col
            .text-h5.font-weight-light Uncommitted Changes
            .text-caption.font-weight-light Branch: {{branch}}
          v-row(no-gutters justify='end' align='center')
            bionic-metrics-saving.text-subtitle-1(:saving-status='savingStatus')
            v-btn(icon @click='close')
              v-icon close
      bionic-metrics-uncommitted-changes(:changes='changes' @diff='diff' @discard='discard')
      bionic-metrics-page-loading.diff-editor(v-if='loading')
      bionic-metrics-monaco-diff-editor.diff-editor(
        v-if='open && selected && !loading'
        v-model='selected.code'
        @input='triggerSave'
        :original='original'
        :type='selected.type'
        :guid='selected.guid'
        :gitName='selected.gitName')
      v-card-actions
        v-text-field(v-if='uncommitted' :disabled='committing' type='text' label='Commit Message' hint='I changed xyz because of abc' v-model='message' required autofocus @keyup.enter="commit").mr-4
        v-spacer(v-if='!uncommitted')
        v-btn(text type='submit' color='primary' :disabled='isSaving || !uncommitted || !message' :loading='committing' @click.stop.prevent='commit') {{commitButtonText}}
</template>
<script>
import {get, size} from 'lodash';
import {mapGetters} from 'vuex';
import BionicMetricsMonacoDiffEditor from './MonacoDiffEditor';
import BionicMetricsSaving from './Saving';
import BionicMetricsPageLoading from './PageLoading';
import GitHub from '../api/github';
import BionicMetricsUncommittedChanges from './UncommittedChanges';
import Notifications from '../services/notification.service';
import SaveMixin from '../mixins/save.mixin';
import AMacro from '../api/amacro';
import Test from '../api/test';

export default {
  name: 'BionicMetricsGitUncommitted',
  components: {BionicMetricsUncommittedChanges, BionicMetricsPageLoading, BionicMetricsSaving, BionicMetricsMonacoDiffEditor},
  mixins: [SaveMixin],
  props: {
    value: {
      required: true
    }
  },
  asyncComputed: {
    changes() {
      if (!this.gitMode || !this.value) return [];
      return GitHub.changes();
    }
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(value) {
        this.selected = null;
        this.$emit('input', value);
      }
    },
    saveObject() {
      return this.selected;
    },
    gitMode() {
      return get(this.account, 'gitMode');
    },
    branch() {
      return get(this.user, 'gitBranch') || 'master';
    },
    uncommitted() {
      return !!size(this.changes);
    },
    commitButtonText() {
      return this.uncommitted ? 'Commit' : 'Nothing To Commit';
    },
    isSaving() {
      return this.savingStatus === 'SAVING';
    },
    ...mapGetters({
      user: 'getUser',
      account: 'getAccount',
      savingStatus: 'getSavingStatus'
    })
  },
  methods: {
    close() {
      this.open = false;
    },
    clearDirty() {
      console.log('Save mixin saved and we are no longer dirty');
    },
    async save(item) {
      const model = item.type === 'module' ? AMacro : Test;
      // going to pair down update to just these fields for now, since we got this object from aggregation and it might be slightly different.
      const update = {guid: item.guid, code: item.code, gitName: item.gitName};
      const {uncommitted} = await model.update(item.guid, update);
      if (uncommitted === false) {
        console.log('Update says code matches git so refresh');
        await this.refreshChanges();
      }
    },
    async discard(item) {
      if (!(await Notifications.confirm('Discard', `Are you sure you want to discard changes for <strong>${item.name}</strong>?`))) return;
      try {
        await GitHub.discard(item.type, item.guid);
        await this.refreshChanges();
      } catch (error) {
        console.error(error);
      }
    },
    async diff(item) {
      try {
        this.loading = true;
        this.selected = item;
        this.original = await GitHub.contents(this.selected.type, this.selected.gitName);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async commit() {
      try {
        this.committing = true;
        await GitHub.commitMultiple(this.message, this.changes);
        this.message = null;
        this.selected = null;
        await Notifications.toast(`Changes successfully committed to <strong>${this.branch}</strong>`);
        this.close();
      } catch (error) {
        console.error(error);
      } finally {
        this.committing = false;
      }
    },
    async refreshChanges() {
      this.$asyncComputed.changes.update(); // downside of using async computed
      this.selected = null;
    }
  },
  data() {
    return {
      loading: false,
      selected: null,
      message: null,
      committing: false,
      render: 0
    };
  }
};
</script>

<style scoped lang='scss'>
  .loading {
    height: 70vh;
  }

  .diff-editor {
    min-height: 50vh;
    height: 50vh;
    width: 100%;
  }
</style>
